"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { motion, useMotionValue } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const AUTO_SCROLL_SPEED = 40; // pixels per second

const Gallery4 = ({
  title = "Case Studies",
  description = "Discover how leading companies and developers are leveraging modern web technologies to build exceptional digital experiences.",
  items = [],
}) => {
  const duplicatedItems = [...items, ...items];
  const x = useMotionValue(0);
  const trackRef = useRef(null);
  const halfWidthRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isHoveringRef = useRef(false);

  // Measure half the track's width (one full un-duplicated set of items) so
  // we know where to wrap x back around for a seamless infinite loop.
  useEffect(() => {
    const measure = () => {
      if (trackRef.current) {
        halfWidthRef.current = trackRef.current.scrollWidth / 2;
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [items]);

  // Whenever x changes — from autoplay, a drag, or drag-release momentum —
  // wrap it back into range so the loop never runs out of duplicated content,
  // whichever direction it's moving.
  useEffect(() => {
    const unsubscribe = x.on("change", (latest) => {
      const half = halfWidthRef.current;
      if (!half) return;
      if (latest <= -half) x.set(latest + half);
      else if (latest > 0) x.set(latest - half);
    });
    return unsubscribe;
  }, [x]);

  // Autoplay loop — only advances when the user isn't dragging or hovering,
  // same pause behavior as before.
  useEffect(() => {
    let raf;
    let lastTime = null;
    const tick = (time) => {
      if (lastTime == null) lastTime = time;
      const dt = (time - lastTime) / 1000;
      lastTime = time;

      if (!isDraggingRef.current && !isHoveringRef.current) {
        x.set(x.get() - AUTO_SCROLL_SPEED * dt);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [x]);

  const handleMouseEnter = useCallback(() => {
    isHoveringRef.current = true;
  }, []);

  const handleMouseLeave = useCallback(() => {
    isHoveringRef.current = false;
  }, []);

  return (
    <section className="py-24 md:py-32 overflow-hidden bg-white">
      <div className="container mx-auto px-4 mb-12 md:mb-16">
        <div className="flex flex-col gap-4 max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tighter text-black">
            {title}
          </h2>
          <p className="text-muted-foreground text-base md:text-lg">{description}</p>
        </div>
      </div>

      <div className="relative w-full" style={{ perspective: "1400px" }}>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-40 bg-gradient-to-r from-white to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-40 bg-gradient-to-l from-white to-transparent z-10" />

        <motion.div
          ref={trackRef}
          className="flex gap-6 md:gap-8 w-max will-change-transform transform-gpu cursor-grab active:cursor-grabbing touch-pan-y"
          style={{ x }}
          drag="x"
          dragMomentum={true}
          onDragStart={() => {
            isDraggingRef.current = true;
          }}
          onDragEnd={() => {
            isDraggingRef.current = false;
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {duplicatedItems.map((item, index) => (
            <Link
              to={item.href}
              key={`${item.id}-${index}`}
              className="group relative flex-shrink-0 w-[260px] sm:w-[300px] md:w-[340px] aspect-[3/4] rounded-2xl overflow-hidden shadow-xl"
              style={{ transformStyle: "preserve-3d" }}
            >
              <motion.div
                whileHover={{ scale: 1.04, rotateY: 4, rotateX: -2 }}
                transition={{ type: "spring", stiffness: 220, damping: 22 }}
                className="relative w-full h-full"
                style={{ transformStyle: "preserve-3d" }}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  loading={index < items.length ? "eager" : "lazy"}
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10 transition-opacity duration-300 group-hover:from-black/90 group-hover:via-black/50" />

                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 text-white">
                  <h3 className="text-lg md:text-xl font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-white/80 line-clamp-2">{item.description}</p>
                </div>

                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="inline-flex items-center gap-2 rounded-full bg-transparent border-2 border-white px-8 py-3 text-sm font-semibold text-white shadow-lg backdrop-blur-sm transition-colors hover:bg-white/10">
                    {item.cta || "Read more"}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </motion.div>
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export { Gallery4 };